import React from 'react';
import { Typography, Divider } from 'antd';
import UIComponentRenderer from './ui-kit/UIComponentRenderer';

const { Text, Title } = Typography;

/**
 * DesignKitShowcase - A read-only artboard displaying all design kit components
 * Organized into sections: Atoms, Molecules, Organisms with all state variations
 */
const DesignKitShowcase = ({ designKit, artboardWidth = 375 }) => {
    if (!designKit) {
        return (
            <div style={{ padding: 24, textAlign: 'center' }}>
                <Text style={{ color: '#8b949e' }}>Loading design kit...</Text>
            </div>
        );
    }

    const atoms = designKit?.components?.atoms || [];
    const molecules = designKit?.components?.molecules || [];
    const organisms = designKit?.components?.organisms || [];

    // State variations for organisms
    const organismStates = ['default', 'loading', 'empty', 'error'];

    const sectionStyle = {
        marginBottom: 32
    };

    const labelStyle = {
        color: '#8b949e',
        fontSize: 10,
        fontWeight: 500,
        letterSpacing: '0.5px',
        textTransform: 'uppercase',
        marginBottom: 8,
        display: 'block'
    };

    const componentWrapperStyle = {
        background: '#161b22',
        borderRadius: 8,
        padding: 16,
        marginBottom: 12,
        border: '1px solid #30363d'
    };

    const componentLabelStyle = {
        color: '#58a6ff',
        fontSize: 11,
        marginBottom: 8,
        display: 'block'
    };

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
        }}>
            {/* Header */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 16px',
                marginBottom: 12,
                background: 'rgba(136, 46, 224, 0.1)',
                border: '1px solid #8b5cf6',
                borderRadius: 6
            }}>
                <span style={{ color: '#8b5cf6', fontSize: 16 }}>🎨</span>
                <Text style={{ color: '#c9d1d9', fontWeight: 600 }}>
                    Design Kit Showcase
                </Text>
                <Text style={{ color: '#484f58', fontSize: 12 }}>
                    {artboardWidth}px
                </Text>
            </div>

            {/* Showcase Canvas */}
            <div style={{
                width: artboardWidth,
                background: '#1c2128',
                border: '2px solid #8b5cf6',
                borderRadius: 8,
                padding: 24,
                boxShadow: '0 0 20px rgba(139, 92, 246, 0.15)'
            }}>
                {/* Atoms Section */}
                <div style={sectionStyle}>
                    <Title level={5} style={{ color: '#c9d1d9', margin: 0, marginBottom: 16 }}>
                        Atoms
                    </Title>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
                        {atoms.map(atom => (
                            <div key={atom.type} style={componentWrapperStyle}>
                                <Text style={componentLabelStyle}>{atom.name}</Text>
                                <Text style={labelStyle}>Variants</Text>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                    {(atom.variants || ['default']).slice(0, 3).map(variant => (
                                        <div key={variant}>
                                            <Text style={{ color: '#484f58', fontSize: 9, marginBottom: 4, display: 'block' }}>
                                                {variant}
                                            </Text>
                                            <div style={{
                                                width: '100%',
                                                height: atom.defaultHeight || 40,
                                                position: 'relative'
                                            }}>
                                                <UIComponentRenderer
                                                    componentType={atom.type}
                                                    state="default"
                                                    contentOverrides={{ variant }}
                                                    deviceType="mobile"
                                                    designKit={designKit}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <Divider style={{ borderColor: '#30363d', margin: '24px 0' }} />

                {/* Molecules Section */}
                <div style={sectionStyle}>
                    <Title level={5} style={{ color: '#c9d1d9', margin: 0, marginBottom: 16 }}>
                        Molecules
                    </Title>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
                        {molecules.map(molecule => (
                            <div key={molecule.type} style={componentWrapperStyle}>
                                <Text style={componentLabelStyle}>{molecule.name}</Text>
                                <Text style={labelStyle}>Variants</Text>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                    {(molecule.variants || ['default']).map(variant => (
                                        <div key={variant}>
                                            <Text style={{ color: '#484f58', fontSize: 9, marginBottom: 4, display: 'block' }}>
                                                {variant}
                                            </Text>
                                            <div style={{
                                                width: '100%',
                                                height: molecule.defaultHeight || 48,
                                                position: 'relative'
                                            }}>
                                                <UIComponentRenderer
                                                    componentType={molecule.type}
                                                    state="default"
                                                    contentOverrides={{ variant }}
                                                    deviceType="mobile"
                                                    designKit={designKit}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <Divider style={{ borderColor: '#30363d', margin: '24px 0' }} />

                {/* Organisms Section */}
                <div style={sectionStyle}>
                    <Title level={5} style={{ color: '#c9d1d9', margin: 0, marginBottom: 16 }}>
                        Organisms
                    </Title>

                    {organisms.map(organism => (
                        <div key={organism.type} style={componentWrapperStyle}>
                            <Text style={componentLabelStyle}>{organism.name}</Text>
                            <Text style={labelStyle}>Variants</Text>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                {(organism.variants || ['default']).map(variant => (
                                    <div key={variant}>
                                        <Text style={{
                                            color: '#8b949e',
                                            fontSize: 10,
                                            marginBottom: 6,
                                            display: 'block',
                                            textTransform: 'capitalize'
                                        }}>
                                            {variant}
                                        </Text>
                                        <div style={{
                                            width: '100%',
                                            minHeight: organism.defaultHeight?.mobile || 60,
                                            position: 'relative'
                                        }}>
                                            <UIComponentRenderer
                                                componentType={organism.type}
                                                state="default"
                                                contentOverrides={{ variant }}
                                                deviceType="mobile"
                                                designKit={designKit}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Tokens Section */}
                <Divider style={{ borderColor: '#30363d', margin: '24px 0' }} />

                <div style={sectionStyle}>
                    <Title level={5} style={{ color: '#c9d1d9', margin: 0, marginBottom: 16 }}>
                        Design Tokens
                    </Title>

                    {/* Colors */}
                    <div style={componentWrapperStyle}>
                        <Text style={componentLabelStyle}>Colors</Text>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                            {(designKit?.tokens?.colors || []).map((color) => (
                                <div key={color.name} style={{ textAlign: 'center' }}>
                                    <div style={{
                                        width: 40,
                                        height: 40,
                                        borderRadius: 8,
                                        background: color.value,
                                        border: '1px solid #30363d',
                                        marginBottom: 4
                                    }} />
                                    <Text style={{ color: '#8b949e', fontSize: 9 }}>{color.name}</Text>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Typography */}
                    <div style={componentWrapperStyle}>
                        <Text style={componentLabelStyle}>Typography</Text>
                        {(designKit?.tokens?.typography || []).map((typo) => (
                            <div key={typo.name} style={{ marginBottom: 8 }}>
                                <Text style={{
                                    color: '#c9d1d9',
                                    fontSize: typo.sizes?.mobile || 14,
                                    fontWeight: typo.weight,
                                    display: 'block'
                                }}>
                                    {typo.name} - {typo.sizes?.mobile}px / {typo.weight}
                                </Text>
                            </div>
                        ))}
                    </div>

                    {/* Spacing */}
                    <div style={componentWrapperStyle}>
                        <Text style={componentLabelStyle}>Spacing Scale</Text>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                            {(designKit?.tokens?.spacing || []).map((space) => (
                                <div key={space.name} style={{ textAlign: 'center' }}>
                                    <div style={{
                                        width: space.values?.mobile || 8,
                                        height: space.values?.mobile || 8,
                                        background: '#58a6ff',
                                        borderRadius: 4,
                                        marginBottom: 4
                                    }} />
                                    <Text style={{ color: '#8b949e', fontSize: 9 }}>{space.name}</Text>
                                    <Text style={{ color: '#484f58', fontSize: 8, display: 'block' }}>{space.values?.mobile}px</Text>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DesignKitShowcase;
